import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  usePharmacySupplierTrash,
  useRestorePharmacySupplier,
  usePermanentlyDeletePharmacySupplier,
  usePharmacyPurchaseOrderTrash,
  useRestorePharmacyPurchaseOrder,
  usePermanentlyDeletePharmacyPurchaseOrder,
  usePharmacyGoodsReceiptTrash,
  useRestorePharmacyGoodsReceipt,
  usePermanentlyDeletePharmacyGoodsReceipt,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { PharmacyLayout } from './PharmacyLayout';
import {
  ArrowLeft,
  Trash2,
  RotateCcw,
  Loader2,
  AlertCircle,
  Search,
  X,
  Truck,
  ShoppingCart,
  PackageCheck,
} from 'lucide-react';

const LIMIT = 20;

type TabKey = 'suppliers' | 'purchase-orders' | 'goods-receipts';

const errorMessage = (err: unknown, fallback: string) =>
  (err as { message?: string } | undefined)?.message || fallback;

const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString() : '—');
const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : '—');

/** Search + pagination + selection state shared by every trash tab. */
function useTrashTab() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  return {
    search,
    setSearch,
    debouncedSearch,
    page,
    setPage,
    selectedIds,
    setSelectedIds,
    confirmOpen,
    setConfirmOpen,
    headerCheckboxRef,
  };
}

/** Select-all helpers for the current page of trash rows. */
function useTrashSelection(
  items: Array<{ id: string }>,
  selectedIds: string[],
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>,
) {
  const allSelected = items.length > 0 && items.every((i) => selectedIds.includes(i.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected, headerCheckboxRef]);

  const toggleAll = () =>
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((id) => !items.some((i) => i.id === id))
        : Array.from(new Set([...prev, ...items.map((i) => i.id)])),
    );

  const toggleOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return { allSelected, someSelected, toggleAll, toggleOne };
}

function TrashToolbar({ search, onSearch }: { search: string; onSearch: (value: string) => void }) {
  return (
    <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          placeholder="Search trashed records..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        {search && (
          <button
            onClick={() => onSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
            title="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function SelectionBar({
  count,
  busy,
  onRestore,
  onDelete,
  onClear,
}: {
  count: number;
  busy: boolean;
  onRestore: () => void;
  onDelete: () => void;
  onClear: () => void;
}) {
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm text-text">
        <span className="font-medium">{count}</span> selected
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRestore}
          disabled={busy}
          className="px-3 py-1.5 bg-success text-white rounded-lg hover:bg-success/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
          Restore
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
        >
          <Trash2 size={14} />
          Delete permanently
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          <X size={14} />
          Clear
        </button>
      </div>
    </div>
  );
}

function HeaderCheckbox({
  checkboxRef,
  checked,
  onChange,
  disabled,
}: {
  checkboxRef: React.RefObject<HTMLInputElement | null>;
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
}) {
  return (
    <th className="px-4 py-3 w-10 text-left">
      <input
        ref={checkboxRef}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
        title="Select all"
      />
    </th>
  );
}

function RowCheckbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <td className="px-4 py-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
      />
    </td>
  );
}

function RowActions({
  onRestore,
  onDelete,
  restorePending,
  deletePending,
}: {
  onRestore: () => void;
  onDelete: () => void;
  restorePending: boolean;
  deletePending: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={onRestore}
        disabled={restorePending}
        className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
        title="Restore"
      >
        <RotateCcw size={16} />
      </button>
      <button
        onClick={onDelete}
        disabled={deletePending}
        className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
        title="Delete permanently"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suppliers tab
// ---------------------------------------------------------------------------
function SuppliersTrashTab() {
  const tab = useTrashTab();
  const { data, isLoading, error } = usePharmacySupplierTrash(
    tab.page,
    LIMIT,
    tab.debouncedSearch.trim() || undefined,
  );
  const suppliers = data?.data || [];
  const meta = data?.meta;

  const restore = useRestorePharmacySupplier();
  const permanentDelete = usePermanentlyDeletePharmacySupplier();
  const [busy, setBusy] = useState(false);

  const { allSelected, toggleAll, toggleOne } = useTrashSelection(
    suppliers,
    tab.selectedIds,
    tab.setSelectedIds,
    tab.headerCheckboxRef,
  );

  const handleSingleRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
      toast.success('Supplier restored');
      tab.setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to restore supplier'));
    }
  };

  const handleBulk = async (mode: 'restore' | 'delete') => {
    const ids = [...tab.selectedIds];
    setBusy(true);
    try {
      if (mode === 'restore') {
        for (const id of ids) await restore.mutateAsync(id);
        toast.success(`Restored ${ids.length} supplier(s)`);
      } else {
        for (const id of ids) await permanentDelete.mutateAsync(id);
        toast.success(`Permanently deleted ${ids.length} supplier(s)`);
      }
      tab.setSelectedIds([]);
    } catch (err) {
      toast.error(errorMessage(err, `Failed to ${mode} some suppliers`));
    } finally {
      setBusy(false);
      tab.setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      <TrashToolbar search={tab.search} onSearch={tab.setSearch} />

      {tab.selectedIds.length > 0 && (
        <SelectionBar
          count={tab.selectedIds.length}
          busy={busy}
          onRestore={() => handleBulk('restore')}
          onDelete={() => tab.setConfirmOpen(true)}
          onClear={() => tab.setSelectedIds([])}
        />
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed suppliers</span>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <HeaderCheckbox
                  checkboxRef={tab.headerCheckboxRef}
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={suppliers.length === 0}
                />
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : suppliers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                    {tab.debouncedSearch ? 'No trashed suppliers match your search.' : 'Supplier trash is empty.'}
                  </td>
                </tr>
              ) : (
                suppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-background/50 transition-colors">
                    <RowCheckbox checked={tab.selectedIds.includes(supplier.id)} onChange={() => toggleOne(supplier.id)} />
                    <td className="px-4 py-3 font-mono text-text-muted">{supplier.supplierCode}</td>
                    <td className="px-4 py-3 font-medium text-text">{supplier.name}</td>
                    <td className="px-4 py-3 text-text-muted">{supplier.contactPerson || '—'}</td>
                    <td className="px-4 py-3 text-text-muted">{formatDateTime(supplier.deletedAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <RowActions
                        onRestore={() => handleSingleRestore(supplier.id)}
                        onDelete={() => permanentDelete.mutate(supplier.id)}
                        restorePending={restore.isPending}
                        deletePending={permanentDelete.isPending}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={meta?.page || tab.page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={LIMIT}
          onPageChange={tab.setPage}
          itemName="trashed suppliers"
        />
      </div>

      <ConfirmDialog
        open={tab.confirmOpen}
        title={`Delete ${tab.selectedIds.length} supplier${tab.selectedIds.length === 1 ? '' : 's'} permanently?`}
        description="This permanently removes the selected suppliers and cannot be undone."
        confirmLabel={`Delete ${tab.selectedIds.length}`}
        loading={busy}
        onConfirm={() => handleBulk('delete')}
        onCancel={() => tab.setConfirmOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Purchase orders tab
// ---------------------------------------------------------------------------
function PurchaseOrdersTrashTab() {
  const tab = useTrashTab();
  const { data, isLoading, error } = usePharmacyPurchaseOrderTrash(
    tab.page,
    LIMIT,
    tab.debouncedSearch.trim() || undefined,
  );
  const orders = data?.data || [];
  const meta = data?.meta;

  const restore = useRestorePharmacyPurchaseOrder();
  const permanentDelete = usePermanentlyDeletePharmacyPurchaseOrder();
  const [busy, setBusy] = useState(false);

  const { allSelected, toggleAll, toggleOne } = useTrashSelection(
    orders,
    tab.selectedIds,
    tab.setSelectedIds,
    tab.headerCheckboxRef,
  );

  const handleSingleRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
      toast.success('Purchase order restored');
      tab.setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to restore purchase order'));
    }
  };

  const handleBulk = async (mode: 'restore' | 'delete') => {
    const ids = [...tab.selectedIds];
    setBusy(true);
    try {
      if (mode === 'restore') {
        for (const id of ids) await restore.mutateAsync(id);
        toast.success(`Restored ${ids.length} purchase order(s)`);
      } else {
        for (const id of ids) await permanentDelete.mutateAsync(id);
        toast.success(`Permanently deleted ${ids.length} purchase order(s)`);
      }
      tab.setSelectedIds([]);
    } catch (err) {
      toast.error(errorMessage(err, `Failed to ${mode} some purchase orders`));
    } finally {
      setBusy(false);
      tab.setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      <TrashToolbar search={tab.search} onSearch={tab.setSearch} />

      {tab.selectedIds.length > 0 && (
        <SelectionBar
          count={tab.selectedIds.length}
          busy={busy}
          onRestore={() => handleBulk('restore')}
          onDelete={() => tab.setConfirmOpen(true)}
          onClear={() => tab.setSelectedIds([])}
        />
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed purchase orders</span>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <HeaderCheckbox
                  checkboxRef={tab.headerCheckboxRef}
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={orders.length === 0}
                />
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">PO Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Supplier</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                    {tab.debouncedSearch ? 'No trashed purchase orders match your search.' : 'Purchase order trash is empty.'}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-background/50 transition-colors">
                    <RowCheckbox checked={tab.selectedIds.includes(order.id)} onChange={() => toggleOne(order.id)} />
                    <td className="px-4 py-3 font-mono font-medium text-text">{order.poNumber}</td>
                    <td className="px-4 py-3 text-text">{order.supplier?.name || '—'}</td>
                    <td className="px-4 py-3 text-right font-medium text-text">
                      {Number(order.totalAmount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{formatDateTime(order.deletedAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <RowActions
                        onRestore={() => handleSingleRestore(order.id)}
                        onDelete={() => permanentDelete.mutate(order.id)}
                        restorePending={restore.isPending}
                        deletePending={permanentDelete.isPending}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={meta?.page || tab.page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={LIMIT}
          onPageChange={tab.setPage}
          itemName="trashed purchase orders"
        />
      </div>

      <ConfirmDialog
        open={tab.confirmOpen}
        title={`Delete ${tab.selectedIds.length} purchase order${tab.selectedIds.length === 1 ? '' : 's'} permanently?`}
        description="This permanently removes the selected orders and cannot be undone."
        confirmLabel={`Delete ${tab.selectedIds.length}`}
        loading={busy}
        onConfirm={() => handleBulk('delete')}
        onCancel={() => tab.setConfirmOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Goods receipts tab
// ---------------------------------------------------------------------------
function GoodsReceiptsTrashTab() {
  const tab = useTrashTab();
  const { data, isLoading, error } = usePharmacyGoodsReceiptTrash(
    tab.page,
    LIMIT,
    tab.debouncedSearch.trim() || undefined,
  );
  const receipts = data?.data || [];
  const meta = data?.meta;

  const restore = useRestorePharmacyGoodsReceipt();
  const permanentDelete = usePermanentlyDeletePharmacyGoodsReceipt();
  const [busy, setBusy] = useState(false);

  const { allSelected, toggleAll, toggleOne } = useTrashSelection(
    receipts,
    tab.selectedIds,
    tab.setSelectedIds,
    tab.headerCheckboxRef,
  );

  const handleSingleRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
      toast.success('Goods receipt restored');
      tab.setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to restore goods receipt'));
    }
  };

  const handleBulk = async (mode: 'restore' | 'delete') => {
    const ids = [...tab.selectedIds];
    setBusy(true);
    try {
      if (mode === 'restore') {
        for (const id of ids) await restore.mutateAsync(id);
        toast.success(`Restored ${ids.length} goods receipt(s)`);
      } else {
        let done = 0;
        for (const id of ids) {
          try {
            await permanentDelete.mutateAsync(id);
            done += 1;
          } catch (err) {
            toast.error(errorMessage(err, 'Failed to delete a goods receipt'));
          }
        }
        if (done > 0) toast.success(`Permanently deleted ${done} goods receipt(s)`);
      }
      tab.setSelectedIds([]);
    } finally {
      setBusy(false);
      tab.setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      <TrashToolbar search={tab.search} onSearch={tab.setSearch} />

      {tab.selectedIds.length > 0 && (
        <SelectionBar
          count={tab.selectedIds.length}
          busy={busy}
          onRestore={() => handleBulk('restore')}
          onDelete={() => tab.setConfirmOpen(true)}
          onClear={() => tab.setSelectedIds([])}
        />
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed goods receipts</span>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <HeaderCheckbox
                  checkboxRef={tab.headerCheckboxRef}
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={receipts.length === 0}
                />
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">GRN Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Received</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : receipts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                    {tab.debouncedSearch ? 'No trashed goods receipts match your search.' : 'Goods receipt trash is empty.'}
                  </td>
                </tr>
              ) : (
                receipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-background/50 transition-colors">
                    <RowCheckbox
                      checked={tab.selectedIds.includes(receipt.id)}
                      onChange={() => toggleOne(receipt.id)}
                    />
                    <td className="px-4 py-3 font-mono font-medium text-text">{receipt.grnNumber}</td>
                    <td className="px-4 py-3 text-text">{receipt.supplier?.name || '—'}</td>
                    <td className="px-4 py-3 text-text-muted">{formatDate(receipt.receivedDate)}</td>
                    <td className="px-4 py-3 text-right font-medium text-text">
                      {Number(receipt.totalAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{formatDateTime(receipt.deletedAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <RowActions
                        onRestore={() => handleSingleRestore(receipt.id)}
                        onDelete={() =>
                          permanentDelete.mutate(receipt.id, {
                            onError: (err) => toast.error(errorMessage(err, 'Failed to delete goods receipt')),
                          })
                        }
                        restorePending={restore.isPending}
                        deletePending={permanentDelete.isPending}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={meta?.page || tab.page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={LIMIT}
          onPageChange={tab.setPage}
          itemName="trashed goods receipts"
        />
      </div>

      <ConfirmDialog
        open={tab.confirmOpen}
        title={`Delete ${tab.selectedIds.length} goods receipt${tab.selectedIds.length === 1 ? '' : 's'} permanently?`}
        description="Stock received by these receipts is reversed where still available. Receipts whose stock has been issued are skipped."
        confirmLabel={`Delete ${tab.selectedIds.length}`}
        loading={busy}
        onConfirm={() => handleBulk('delete')}
        onCancel={() => tab.setConfirmOpen(false)}
      />
    </div>
  );
}

const tabs: Array<{ key: TabKey; label: string; icon: typeof Truck }> = [
  { key: 'suppliers', label: 'Suppliers', icon: Truck },
  { key: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
  { key: 'goods-receipts', label: 'Goods Receipts', icon: PackageCheck },
];

export function PharmacyPurchasingTrash() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('suppliers');

  return (
    <PharmacyLayout
      header={
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pharmacy/purchases')}
            className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger/10 rounded-lg">
              <Trash2 className="w-6 h-6 text-danger" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text">Purchasing Trash</h1>
              <p className="text-sm text-text-muted">Restore or permanently delete suppliers, orders and receipts</p>
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-surface border border-border rounded-xl p-2 shadow-sm overflow-x-auto">
          <nav className="flex items-center gap-1 min-w-max">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  tab === t.key
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-text-muted hover:text-text hover:bg-background'
                }`}
              >
                <t.icon size={16} />
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {tab === 'suppliers' && <SuppliersTrashTab />}
        {tab === 'purchase-orders' && <PurchaseOrdersTrashTab />}
        {tab === 'goods-receipts' && <GoodsReceiptsTrashTab />}
      </div>
    </PharmacyLayout>
  );
}
